import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import { useQuery } from '@tanstack/react-query';

import {
  getPlatformAdminActivityEvents,
  getPlatformAdminAuditEvents,
  getPlatformAdminContext,
  getPlatformAdminObservabilityEvents,
  getPlatformAdminOverview,
  getPlatformAdminTenantUsers,
  getPlatformAdminTenants,
  getPlatformAdminUsers,
} from '../services/platformAdminService';
import type {
  PlatformActivityEventItem,
  PlatformAdminAuditEventItem,
  PlatformAdminUserListItem,
  PlatformObservabilityEventItem,
  PlatformTenantListItem,
} from '../types/platformAdmin';

type ActivityLens =
  | 'attention'
  | 'all'
  | 'pricing'
  | 'industrial_costs'
  | 'poker_study'
  | 'imports'
  | 'permissions';

const ACTIVITY_LENS_OPTIONS: Array<{
  value: ActivityLens;
  label: string;
}> = [
  { value: 'attention', label: 'Requiere atención' },
  { value: 'all', label: 'Todo' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'industrial_costs', label: 'Industrial' },
  { value: 'poker_study', label: 'Poker' },
  { value: 'imports', label: 'Cargas' },
  { value: 'permissions', label: 'Permisos' },
];

function getActivityLensParams(lens: ActivityLens) {
  if (lens === 'attention') {
    return {
      outcome: 'failed,partial,blocked',
      severity: 'warning,error,critical',
    };
  }

  if (lens === 'pricing') {
    return {
      app_module: 'pricing',
    };
  }

  if (lens === 'industrial_costs') {
    return {
      app_module: 'industrial_costs',
    };
  }

  if (lens === 'poker_study') {
    return {
      app_module: 'poker_study',
    };
  }

  if (lens === 'imports') {
    return {
      area: 'data_upload,import_center,production_reports',
    };
  }

  if (lens === 'permissions') {
    return {
      event_type: [
        'permission_denied',
        'inactive_user_blocked',
        'tenant_context_failed',
        'module_not_enabled',
        'role_required',
      ].join(','),
    };
  }

  return {};
}

type PlatformAdminTab =
  | 'overview'
  | 'tenants'
  | 'users'
  | 'activity'
  | 'observability'
  | 'audit';

const darkPaperSx = {
  bgcolor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 4,
};

const adminTableContainerSx = {
  maxHeight: {
    xs: 360,
    md: 520,
  },
  overflow: 'auto',
  borderRadius: 2.5,
  '& .MuiTableCell-stickyHeader': {
    bgcolor: '#1e293b',
    color: '#94a3b8',
    borderBottom: '1px solid #475569',
    zIndex: 2,
  },
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    bgcolor: 'rgba(15,23,42,0.7)',
  },
  '&::-webkit-scrollbar-thumb': {
    bgcolor: 'rgba(148,163,184,0.34)',
    borderRadius: 999,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    bgcolor: 'rgba(148,163,184,0.54)',
  },
};

function useAdminTablePagination<T>(
  rows: T[],
  options?: {
    initialRowsPerPage?: number;
    rowsPerPageOptions?: number[];
  },
) {
  const rowsPerPageOptions = options?.rowsPerPageOptions ?? [10, 25, 50];
  const initialRowsPerPage =
    options?.initialRowsPerPage ?? rowsPerPageOptions[0];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const totalRows = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);

  useEffect(() => {
    setPage(0);
  }, [totalRows, rowsPerPage]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const paginatedRows = useMemo(
    () =>
      rows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
      ),
    [rows, rowsPerPage, safePage],
  );

  const shouldShowPagination = totalRows > rowsPerPageOptions[0];

  const pagination = shouldShowPagination ? (
    <TablePagination
      component="div"
      count={totalRows}
      page={safePage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
      }}
      labelRowsPerPage="Filas por página"
      labelDisplayedRows={({ from, to, count }) =>
        `${from}-${to} de ${count}`
      }
      sx={{
        borderTop: '1px solid #334155',
        color: '#94a3b8',
        minHeight: 44,
        '& .MuiTablePagination-toolbar': {
          minHeight: 44,
          px: 1,
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          m: 0,
          fontSize: '0.78rem',
          fontWeight: 800,
        },
        '& .MuiTablePagination-select': {
          color: '#f8fafc',
          fontWeight: 850,
        },
        '& .MuiTablePagination-actions button': {
          color: '#94a3b8',
        },
        '& .MuiTablePagination-actions button:hover': {
          color: '#38bdf8',
          bgcolor: 'rgba(56, 189, 248, 0.08)',
        },
        '& .MuiTablePagination-actions button.Mui-disabled': {
          color: '#475569',
        },
        '& .MuiSvgIcon-root': {
          color: '#94a3b8',
        },
      }}
    />
  ) : null;

  return {
    paginatedRows,
    pagination,
  };
}

function formatDate(value?: string | null) {
  if (!value) return '—';

  try {
    return new Intl.DateTimeFormat('es-ES', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatMetricValue(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat('es-ES').format(value);
}

function getStatusChipColor(status?: string | null) {
  if (!status) {
    return {
      bgcolor: '#334155',
      color: '#cbd5e1',
    };
  }

  if (['active', 'enabled', 'success', 'ok', 'resolved'].includes(status)) {
    return {
      bgcolor: 'rgba(34, 197, 94, 0.14)',
      color: '#86efac',
    };
  }

  if (['trial', 'pilot', 'warning', 'open'].includes(status)) {
    return {
      bgcolor: 'rgba(250, 204, 21, 0.14)',
      color: '#fde68a',
    };
  }

  if (['error', 'failed', 'suspended', 'inactive'].includes(status)) {
    return {
      bgcolor: 'rgba(248, 113, 113, 0.14)',
      color: '#fca5a5',
    };
  }

  return {
    bgcolor: '#334155',
    color: '#cbd5e1',
  };
}

function CompactStatBlock({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  detail?: string;
  tone?: 'default' | 'danger' | 'success';
}) {
  const toneColor =
    tone === 'danger'
      ? '#fca5a5'
      : tone === 'success'
        ? '#86efac'
        : '#7dd3fc';

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: 'rgba(2, 6, 23, 0.28)',
        border: '1px solid rgba(148, 163, 184, 0.14)',
      }}
    >
      <Typography
        noWrap
        sx={{
          color: '#94a3b8',
          fontSize: '0.75rem',
          fontWeight: 850,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.35 }}>
        <Typography
          sx={{
            color: '#f8fafc',
            fontSize: '1.55rem',
            lineHeight: 1,
            fontWeight: 950,
            letterSpacing: '-0.04em',
          }}
        >
          {typeof value === 'number' ? formatMetricValue(value) : value}
        </Typography>

        {detail ? (
          <Typography
            noWrap
            sx={{
              color: toneColor,
              fontSize: '0.72rem',
              fontWeight: 800,
            }}
          >
            {detail}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

function SectionHeader({
  icon,
  title,
  description,
  right,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  right?: React.ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 3 }}
    >
      <Box>
        <Stack direction="row" spacing={1.25} alignItems="center">
          {icon}
          <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.75 }}>
          {description}
        </Typography>
      </Box>

      {right}
    </Stack>
  );
}

function EmptyTableState({
  message,
}: {
  message: string;
}) {
  return (
    <Box sx={{ py: 5, textAlign: 'center' }}>
      <Typography sx={{ color: '#94a3b8', fontWeight: 700 }}>
        {message}
      </Typography>
    </Box>
  );
}

function getTenantExternalRef(
  tenant: PlatformTenantListItem,
  key: string,
) {
  const value = tenant.external_refs?.[key];

  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

function getUsersForTenant(
  tenant: PlatformTenantListItem,
  users: PlatformAdminUserListItem[],
) {
  const pricingAccountId = getTenantExternalRef(
    tenant,
    'pricing_account_id',
  );
  const industrialTenantId = getTenantExternalRef(
    tenant,
    'ic_tenant_id',
  );

  return users.filter((user) => {
    if (
      tenant.tenant_type === 'pricing_account' &&
      pricingAccountId
    ) {
      return user.pricing_access.some(
        (access) =>
          access.pricing_account_id === pricingAccountId,
      );
    }

    if (
      tenant.tenant_type === 'industrial_costs_tenant' &&
      industrialTenantId
    ) {
      return user.industrial_access.some(
        (access) =>
          access.tenant_id === industrialTenantId,
      );
    }

    return false;
  });
}

function getTenantOwnerLabel(
  tenant: PlatformTenantListItem,
  users: PlatformAdminUserListItem[],
) {
  if (!tenant.owner_user_id) {
    return '—';
  }

  const ownerUser = users.find(
    (user) => user.user_id === tenant.owner_user_id,
  );

  if (!ownerUser) {
    return `Usuario ${tenant.owner_user_id.slice(0, 8)}`;
  }

  return (
    ownerUser.email ||
    ownerUser.display_name ||
    ownerUser.full_name ||
    `Usuario ${tenant.owner_user_id.slice(0, 8)}`
  );
}

function getTenantUserRoleLabels(
  tenant: PlatformTenantListItem,
  user: PlatformAdminUserListItem,
) {
  const pricingAccountId = getTenantExternalRef(
    tenant,
    'pricing_account_id',
  );
  const industrialTenantId = getTenantExternalRef(
    tenant,
    'ic_tenant_id',
  );

  if (
    tenant.tenant_type === 'pricing_account' &&
    pricingAccountId
  ) {
    return user.pricing_access
      .filter(
        (access) =>
          access.pricing_account_id === pricingAccountId,
      )
      .map(
        (access) =>
          `Pricing ${access.role || 'member'} · ${access.status || 'active'}`,
      );
  }

  if (
    tenant.tenant_type === 'industrial_costs_tenant' &&
    industrialTenantId
  ) {
    return user.industrial_access
      .filter(
        (access) =>
          access.tenant_id === industrialTenantId,
      )
      .map(
        (access) =>
          `Industrial ${access.role || 'user'} · ${
            access.is_active === false ? 'inactive' : 'active'
          }`,
      );
  }

  return [];
}

function getTimestampSortValue(value?: string | null) {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
}

function getTenantStatusSortWeight(statusValue?: string | null) {
  if (!statusValue) {
    return 2;
  }

  if (['active', 'enabled', 'trial', 'pilot'].includes(statusValue)) {
    return 0;
  }

  if (['warning', 'open'].includes(statusValue)) {
    return 1;
  }

  if (['suspended', 'inactive', 'error', 'failed'].includes(statusValue)) {
    return 3;
  }

  return 2;
}

function sortPlatformTenantsForAdmin(
  tenants: PlatformTenantListItem[],
) {
  return [...tenants].sort((left, right) => {
    const statusDifference =
      getTenantStatusSortWeight(left.status) -
      getTenantStatusSortWeight(right.status);

    if (statusDifference !== 0) {
      return statusDifference;
    }

    const eventsDifference =
      (right.open_events_count ?? 0) -
      (left.open_events_count ?? 0);

    if (eventsDifference !== 0) {
      return eventsDifference;
    }

    const usersDifference =
      (right.users_count ?? 0) -
      (left.users_count ?? 0);

    if (usersDifference !== 0) {
      return usersDifference;
    }

    const activityDifference =
      getTimestampSortValue(right.last_activity_at) -
      getTimestampSortValue(left.last_activity_at);

    if (activityDifference !== 0) {
      return activityDifference;
    }

    return left.name.localeCompare(right.name, 'es', {
      sensitivity: 'base',
    });
  });
}

function sortPlatformUsersForAdmin(
  users: PlatformAdminUserListItem[],
) {
  return [...users].sort((left, right) => {
    const loginDifference =
      getTimestampSortValue(right.last_sign_in_at) -
      getTimestampSortValue(left.last_sign_in_at);

    if (loginDifference !== 0) {
      return loginDifference;
    }

    const leftName =
      left.display_name ||
      left.full_name ||
      left.email ||
      left.user_id;

    const rightName =
      right.display_name ||
      right.full_name ||
      right.email ||
      right.user_id;

    return leftName.localeCompare(rightName, 'es', {
      sensitivity: 'base',
    });
  });
}

function TenantDetailDialog({
  tenant,
  users,
  onClose,
}: {
  tenant: PlatformTenantListItem | null;
  users: PlatformAdminUserListItem[];
  onClose: () => void;
}) {
  const [tenantUserSearchInput, setTenantUserSearchInput] = useState('');

  useEffect(() => {
    setTenantUserSearchInput('');
  }, [tenant?.id]);

  const fallbackRelatedUsers = useMemo(
    () =>
      tenant
        ? sortPlatformUsersForAdmin(
            getUsersForTenant(tenant, users),
          )
        : [],
    [tenant, users],
  );

  const tenantUsersQuery = useQuery({
    queryKey: ['platform-admin-tenant-users', tenant?.id],
    queryFn: () =>
      getPlatformAdminTenantUsers(tenant?.id as string, {
        limit: 100,
        offset: 0,
      }),
    enabled: Boolean(tenant?.id),
    placeholderData: (previousData) => previousData,
    retry: false,
    staleTime: 60 * 1000,
  });

  const relatedUsers = sortPlatformUsersForAdmin(
    tenantUsersQuery.data?.users ?? fallbackRelatedUsers,
  );

  const normalizedTenantUserSearch =
    tenantUserSearchInput.trim().toLowerCase();

  const filteredRelatedUsers = normalizedTenantUserSearch
    ? relatedUsers.filter((user) => {
        const searchableText = [
          user.display_name,
          user.full_name,
          user.email,
          user.company_name,
          user.user_id,
          user.modules.join(' '),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchableText.includes(normalizedTenantUserSearch);
      })
    : relatedUsers;

  const {
    paginatedRows: paginatedRelatedUsers,
    pagination: relatedUsersPagination,
  } = useAdminTablePagination(filteredRelatedUsers);

  const relatedUsersTotal =
    tenantUsersQuery.data?.total ?? relatedUsers.length;

  const tenantUsersIsRefreshing =
    tenantUsersQuery.isFetching && !tenantUsersQuery.isLoading;

  if (!tenant) {
    return null;
  }

  const statusTone = getStatusChipColor(tenant.status);

  return (
    <Dialog
      open={Boolean(tenant)}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: '#0f172a',
          color: '#f8fafc',
          border: '1px solid #334155',
          borderRadius: 4,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>
              {tenant.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              {tenant.tenant_type} · {tenant.source}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={tenant.status}
              sx={{
                fontWeight: 900,
                ...statusTone,
              }}
            />
            <Chip
              size="small"
              label={`${tenant.users_count} usuarios`}
              sx={{
                bgcolor: '#020617',
                color: '#bae6fd',
                fontWeight: 900,
              }}
            />
            <Chip
              size="small"
              label={`${tenant.open_events_count} eventos`}
              sx={{
                bgcolor: '#020617',
                color: '#f8fafc',
                fontWeight: 900,
              }}
            />
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(15,23,42,0.72)',
                border: '1px solid #334155',
                borderRadius: 3,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 900 }}>
                Tipo
              </Typography>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.5 }}>
                {tenant.tenant_type}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(15,23,42,0.72)',
                border: '1px solid #334155',
                borderRadius: 3,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 900 }}>
                Última actividad
              </Typography>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.5 }}>
                {formatDate(tenant.last_activity_at)}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(15,23,42,0.72)',
                border: '1px solid #334155',
                borderRadius: 3,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 900 }}>
                Owner
              </Typography>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.5 }}>
                {getTenantOwnerLabel(tenant, users)}
              </Typography>
            </Paper>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'rgba(15,23,42,0.56)',
              border: '1px solid #334155',
              borderRadius: 3,
            }}
          >
            <Stack spacing={1.5}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Módulos activos
              </Typography>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tenant.modules.length ? tenant.modules.map((moduleKey) => (
                  <Chip
                    key={moduleKey}
                    size="small"
                    label={moduleKey}
                    sx={{
                      bgcolor: '#020617',
                      color: '#bae6fd',
                      fontWeight: 900,
                    }}
                  />
                )) : (
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    No hay módulos asociados.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'rgba(15,23,42,0.56)',
              border: '1px solid #334155',
              borderRadius: 3,
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={1}
              >
                <Box>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Usuarios del tenant
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.25 }}>
                    Mostrando {filteredRelatedUsers.length} de {relatedUsersTotal} usuario{relatedUsersTotal === 1 ? '' : 's'}.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={0.75}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  {tenantUsersIsRefreshing ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#7dd3fc',
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Actualizando…
                    </Typography>
                  ) : null}

                  <TextField
                    size="small"
                    placeholder="Buscar usuario del tenant..."
                    value={tenantUserSearchInput}
                    onChange={(event) => setTenantUserSearchInput(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: { xs: '100%', sm: 280 },
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        bgcolor: '#0f172a',
                        '& fieldset': { borderColor: '#334155' },
                        '&:hover fieldset': { borderColor: '#38bdf8' },
                      },
                    }}
                  />

                  <Chip
                    size="small"
                    label={`${relatedUsersTotal} usuario${relatedUsersTotal === 1 ? '' : 's'}`}
                    sx={{
                      bgcolor: '#020617',
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  />
                </Stack>
              </Stack>

              {tenantUsersQuery.isError ? (
                <Alert severity="warning" variant="outlined">
                  No se pudieron cargar todos los usuarios del tenant. Se muestra la información global disponible.
                </Alert>
              ) : null}

              {relatedUsersTotal > relatedUsers.length ? (
                <Alert severity="info" variant="outlined">
                  Hay más usuarios asociados al tenant de los que se muestran en esta primera carga.
                </Alert>
              ) : null}

              {filteredRelatedUsers.length ? (
                <>
                  <TableContainer sx={adminTableContainerSx}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>
                          Usuario
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>
                          Acceso en este tenant
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>
                          Módulos
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>
                          Último login
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedRelatedUsers.map((user) => {
                        const roleLabels = getTenantUserRoleLabels(tenant, user);

                        return (
                          <TableRow key={user.user_id}>
                            <TableCell sx={{ color: '#f8fafc', borderColor: '#334155', maxWidth: 320 }}>
                              <Stack spacing={0.25}>
                                <Typography noWrap sx={{ color: '#f8fafc', fontWeight: 850 }}>
                                  {user.full_name || user.display_name || user.email || 'Usuario sin nombre'}
                                </Typography>
                                <Typography noWrap variant="caption" sx={{ color: '#94a3b8' }}>
                                  {user.email || 'Sin email disponible'}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ borderColor: '#334155', maxWidth: 360 }}>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {roleLabels.length ? roleLabels.map((roleLabel) => (
                                  <Chip
                                    key={roleLabel}
                                    size="small"
                                    label={roleLabel}
                                    sx={{
                                      bgcolor: 'rgba(56, 189, 248, 0.12)',
                                      color: '#bae6fd',
                                      fontWeight: 800,
                                    }}
                                  />
                                )) : (
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Sin acceso específico visible
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ borderColor: '#334155', maxWidth: 260 }}>
                              <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                                {user.modules.length ? user.modules.map((moduleKey) => (
                                  <Chip
                                    key={moduleKey}
                                    size="small"
                                    label={moduleKey}
                                    sx={{
                                      bgcolor: '#020617',
                                      color: '#cbd5e1',
                                      fontWeight: 800,
                                    }}
                                  />
                                )) : (
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Sin módulos
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155', whiteSpace: 'nowrap' }}>
                              {formatDate(user.last_sign_in_at)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </TableContainer>
                  {relatedUsersPagination}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {normalizedTenantUserSearch
                    ? 'No hay usuarios del tenant que coincidan con la búsqueda.'
                    : 'No se han encontrado usuarios asociados con los datos disponibles.'}
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'rgba(2,6,23,0.42)',
              border: '1px solid #334155',
              borderRadius: 3,
            }}
          >
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
              Referencias técnicas
            </Typography>
            <Typography
              component="pre"
              sx={{
                m: 0,
                color: '#94a3b8',
                fontSize: '0.78rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(
                {
                  id: tenant.id,
                  source: tenant.source,
                  external_refs: tenant.external_refs,
                },
                null,
                2,
              )}
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#bae6fd',
            borderColor: '#334155',
            textTransform: 'none',
            fontWeight: 900,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TenantTable({
  tenants,
  users,
  onSelectTenant,
}: {
  tenants: PlatformTenantListItem[];
  users: PlatformAdminUserListItem[];
  onSelectTenant: (tenant: PlatformTenantListItem) => void;
}) {
  const {
    paginatedRows: paginatedTenants,
    pagination,
  } = useAdminTablePagination(tenants);

  if (!tenants.length) {
    return <EmptyTableState message="No hay tenants o cuentas para mostrar." />;
  }

  return (
    <>
      <TableContainer sx={adminTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Tenant / cuenta</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Tipo</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Estado</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Owner</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Módulos</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }} align="right">Usuarios</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }} align="right">Eventos</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Última actividad</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedTenants.map((tenant) => (
            <TableRow
              key={tenant.id}
              hover
              onClick={() => onSelectTenant(tenant)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(56, 189, 248, 0.04)',
                },
              }}
            >
              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }}>
                <Stack spacing={0.25}>
                  <Typography sx={{ fontWeight: 850 }}>{tenant.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {tenant.source}
                  </Typography>
                </Stack>
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {tenant.tenant_type}
              </TableCell>

              <TableCell sx={{ borderColor: '#334155' }}>
                <Chip
                  size="small"
                  label={tenant.status}
                  sx={{
                    fontWeight: 800,
                    ...getStatusChipColor(tenant.status),
                  }}
                />
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155', maxWidth: 220 }}>
                <Typography noWrap variant="body2" sx={{ fontWeight: 800 }}>
                  {getTenantOwnerLabel(tenant, users)}
                </Typography>
              </TableCell>

              <TableCell sx={{ borderColor: '#334155' }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {tenant.modules.length ? tenant.modules.map((moduleKey) => (
                    <Chip
                      key={moduleKey}
                      size="small"
                      label={moduleKey}
                      sx={{ bgcolor: '#0f172a', color: '#bae6fd', fontWeight: 800 }}
                    />
                  )) : (
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Sin módulos
                    </Typography>
                  )}
                </Stack>
              </TableCell>

              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }} align="right">
                {tenant.users_count}
              </TableCell>

              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }} align="right">
                {tenant.open_events_count}
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {formatDate(tenant.last_activity_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      {pagination}
    </>
  );
}

function getUserRoleChips(user: PlatformAdminUserListItem) {
  const chips: { key: string; label: string }[] = [];

  user.pricing_access.forEach((access, index) => {
    chips.push({
      key: `pricing-${access.pricing_account_id}-${index}`,
      label: `Pricing ${access.role || 'member'}`,
    });
  });

  user.industrial_access.forEach((access, index) => {
    chips.push({
      key: `industrial-${access.tenant_id}-${index}`,
      label: `Industrial ${access.role || 'user'}`,
    });
  });

  user.poker_access.forEach((access, index) => {
    chips.push({
      key: `poker-${access.tenant_id}-${index}`,
      label: `Poker ${access.role || 'user'}`,
    });
  });

  return chips;
}

function UsersTable({
  users,
}: {
  users: PlatformAdminUserListItem[];
}) {
  const {
    paginatedRows: paginatedUsers,
    pagination,
  } = useAdminTablePagination(users);

  if (!users.length) {
    return <EmptyTableState message="No hay usuarios para mostrar." />;
  }

  return (
    <>
      <TableContainer sx={adminTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Usuario</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Empresa</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Módulos</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Roles</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Tier</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }} align="right">Eventos</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Último login</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedUsers.map((user) => {
            const roleChips = getUserRoleChips(user);

            return (
              <TableRow key={user.user_id} hover>
                <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 850 }}>
                      {user.display_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {user.email || 'Sin email disponible'}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                  {user.company_name || '—'}
                </TableCell>

                <TableCell sx={{ borderColor: '#334155' }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {user.modules.length ? user.modules.map((moduleKey) => (
                      <Chip
                        key={moduleKey}
                        size="small"
                        label={moduleKey}
                        sx={{ bgcolor: '#0f172a', color: '#bae6fd', fontWeight: 800 }}
                      />
                    )) : (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Sin módulos
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ borderColor: '#334155', maxWidth: 360 }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {roleChips.length ? roleChips.map((chip) => (
                      <Chip
                        key={chip.key}
                        size="small"
                        label={chip.label}
                        sx={{
                          bgcolor: 'rgba(56, 189, 248, 0.12)',
                          color: '#bae6fd',
                          fontWeight: 800,
                        }}
                      />
                    )) : (
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Sin roles asociados
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ borderColor: '#334155' }}>
                  <Chip
                    size="small"
                    label={user.tier_id || 'sin tier'}
                    sx={{ bgcolor: '#0f172a', color: '#bae6fd', fontWeight: 800 }}
                  />
                </TableCell>

                <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }} align="right">
                  {user.observability_total_count}
                </TableCell>

                <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                  {formatDate(user.last_sign_in_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </TableContainer>
      {pagination}
    </>
  );
}

function ObservabilityTable({
  events,
}: {
  events: PlatformObservabilityEventItem[];
}) {
  const {
    paginatedRows: paginatedEvents,
    pagination,
  } = useAdminTablePagination(events);

  if (!events.length) {
    return <EmptyTableState message="No hay eventos de observabilidad para mostrar." />;
  }

  return (
    <>
      <TableContainer sx={adminTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Evento</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Módulo</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Severidad</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Estado</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Endpoint / ruta</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }} align="right">HTTP</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }} align="right">Veces</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Última vez</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedEvents.map((event) => (
            <TableRow key={event.id} hover>
              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155', maxWidth: 360 }}>
                <Stack spacing={0.25}>
                  <Typography noWrap sx={{ fontWeight: 850 }}>
                    {event.message || event.event_type || 'Evento sin mensaje'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {event.event_type || event.id}
                  </Typography>
                </Stack>
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {event.app_module || '—'}
              </TableCell>

              <TableCell sx={{ borderColor: '#334155' }}>
                <Chip
                  size="small"
                  label={event.severity || 'unknown'}
                  sx={{
                    fontWeight: 800,
                    ...getStatusChipColor(event.severity),
                  }}
                />
              </TableCell>

              <TableCell sx={{ borderColor: '#334155' }}>
                <Chip
                  size="small"
                  label={event.status || 'unknown'}
                  sx={{
                    fontWeight: 800,
                    ...getStatusChipColor(event.status),
                  }}
                />
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155', maxWidth: 260 }}>
                <Typography noWrap variant="body2">
                  {event.backend_endpoint || event.frontend_route || '—'}
                </Typography>
              </TableCell>

              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }} align="right">
                {event.http_status_code || '—'}
              </TableCell>

              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }} align="right">
                {event.occurrence_count}
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {formatDate(event.last_seen_at || event.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      {pagination}
    </>
  );
}

function getActivityMetricNumber(
  event: PlatformActivityEventItem,
  key: string,
) {
  const value = event.metrics?.[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
}

function formatActivityImpact(event: PlatformActivityEventItem) {
  const rowsReceived = getActivityMetricNumber(event, 'rows_received');
  const rowsValid = getActivityMetricNumber(event, 'rows_valid');
  const rowsImported = getActivityMetricNumber(event, 'rows_imported');
  const rowsCreated = getActivityMetricNumber(event, 'rows_created');
  const rowsUpdated = getActivityMetricNumber(event, 'rows_updated');
  const rowsDeactivated = getActivityMetricNumber(event, 'rows_deactivated');
  const rowErrorsCount = getActivityMetricNumber(event, 'row_errors_count') ?? 0;
  const duplicateRowsCount = getActivityMetricNumber(event, 'duplicate_rows_count') ?? 0;
  const rowsSkippedDuplicate = getActivityMetricNumber(
    event,
    'rows_skipped_duplicate_in_file',
  ) ?? 0;

  const warningCount =
    rowErrorsCount +
    duplicateRowsCount +
    rowsSkippedDuplicate;

  const parts: string[] = [];

  if (rowsImported !== null) {
    parts.push(`${formatMetricValue(rowsImported)} importadas`);
  }

  if (rowsCreated !== null || rowsUpdated !== null) {
    const createdLabel = rowsCreated !== null
      ? `${formatMetricValue(rowsCreated)} creadas`
      : null;

    const updatedLabel = rowsUpdated !== null
      ? `${formatMetricValue(rowsUpdated)} actualizadas`
      : null;

    parts.push(
      [createdLabel, updatedLabel]
        .filter(Boolean)
        .join(' · '),
    );
  }

  if (rowsValid !== null && rowsReceived !== null) {
    parts.push(
      `${formatMetricValue(rowsValid)}/${formatMetricValue(rowsReceived)} válidas`,
    );
  } else if (rowsReceived !== null) {
    parts.push(`${formatMetricValue(rowsReceived)} recibidas`);
  }

  if (rowsDeactivated !== null && rowsDeactivated > 0) {
    parts.push(`${formatMetricValue(rowsDeactivated)} desactivadas`);
  }

  if (warningCount > 0) {
    parts.push(`${formatMetricValue(warningCount)} avisos`);
  }

  if (!parts.length) {
    return '—';
  }

  return parts.slice(0, 4).join(' · ');
}

function getShortTechnicalRef(value?: string | null) {
  if (!value) {
    return '—';
  }

  return value.length > 12
    ? `${value.slice(0, 8)}…`
    : value;
}

function getActivityAccountLabel(
  event: PlatformActivityEventItem,
  tenants: PlatformTenantListItem[],
) {
  const matchingTenant = tenants.find((tenant) => {
    const pricingAccountId = getTenantExternalRef(
      tenant,
      'pricing_account_id',
    );
    const pricingDataOwnerUserId = getTenantExternalRef(
      tenant,
      'data_owner_user_id',
    );
    const industrialTenantId = getTenantExternalRef(
      tenant,
      'ic_tenant_id',
    );

    if (
      event.pricing_account_id &&
      pricingAccountId &&
      event.pricing_account_id === pricingAccountId
    ) {
      return true;
    }

    if (
      event.pricing_account_key &&
      pricingDataOwnerUserId &&
      event.pricing_account_key === pricingDataOwnerUserId
    ) {
      return true;
    }

    if (
      event.industrial_tenant_id &&
      industrialTenantId &&
      event.industrial_tenant_id === industrialTenantId
    ) {
      return true;
    }

    if (
      event.platform_tenant_id &&
      tenant.id === event.platform_tenant_id
    ) {
      return true;
    }

    if (
      event.platform_tenant_id &&
      tenant.id === `platform:${event.platform_tenant_id}`
    ) {
      return true;
    }

    return false;
  });

  if (matchingTenant) {
    return matchingTenant.name;
  }

  if (event.pricing_account_key) {
    return `Pricing ${getShortTechnicalRef(event.pricing_account_key)}`;
  }

  if (event.pricing_account_id) {
    return `Pricing ${getShortTechnicalRef(event.pricing_account_id)}`;
  }

  if (event.industrial_tenant_id) {
    return `Industrial Costs ${getShortTechnicalRef(event.industrial_tenant_id)}`;
  }

  if (event.poker_tenant_id) {
    return `Poker ${getShortTechnicalRef(event.poker_tenant_id)}`;
  }

  if (event.platform_tenant_id) {
    return `Platform ${getShortTechnicalRef(event.platform_tenant_id)}`;
  }

  return '—';
}

const ACTIVITY_PRIMARY_METRIC_KEYS = [
  'rows_received',
  'rows_valid',
  'rows_imported',
  'rows_created',
  'rows_updated',
  'rows_deactivated',
  'row_errors_count',
  'duplicate_rows_count',
  'rows_skipped_duplicate_in_file',
  'plan_items_cancelled',
];

const ACTIVITY_METRIC_LABELS: Record<string, string> = {
  rows_received: 'Filas recibidas',
  rows_valid: 'Filas válidas',
  rows_imported: 'Filas importadas',
  rows_created: 'Creadas',
  rows_updated: 'Actualizadas',
  rows_deactivated: 'Desactivadas',
  row_errors_count: 'Errores / avisos',
  duplicate_rows_count: 'Duplicadas',
  rows_skipped_duplicate_in_file: 'Duplicadas omitidas',
  duplicate_manufacturing_orders_in_file_count: 'OFs duplicadas',
  plan_items_cancelled: 'Planificaciones canceladas',
  mapping_fields_count: 'Campos mapeados',
};

function getActivityMetricLabel(key: string) {
  return ACTIVITY_METRIC_LABELS[key] || key.split('_').join(' ');
}

function getActivityDisplayMetricValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatMetricValue(value);
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return formatMetricValue(parsedValue);
    }

    return value.trim();
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  return null;
}

function getActivityMetricEntries(
  event: PlatformActivityEventItem,
  preferredKeys?: string[],
) {
  const metrics = event.metrics ?? {};
  const keys = preferredKeys ?? Object.keys(metrics);

  return keys
    .map((key) => {
      const value = getActivityDisplayMetricValue(metrics[key]);

      if (value === null) {
        return null;
      }

      return {
        key,
        label: getActivityMetricLabel(key),
        value,
      };
    })
    .filter(
      (
        item,
      ): item is {
        key: string;
        label: string;
        value: string;
      } => Boolean(item),
    );
}

function ActivityDetailDialog({
  event,
  tenants,
  onClose,
}: {
  event: PlatformActivityEventItem | null;
  tenants: PlatformTenantListItem[];
  onClose: () => void;
}) {
  if (!event) {
    return null;
  }

  const accountLabel = getActivityAccountLabel(event, tenants);
  const primaryMetricEntries = getActivityMetricEntries(
    event,
    ACTIVITY_PRIMARY_METRIC_KEYS,
  );
  const primaryMetricKeys = new Set(
    primaryMetricEntries.map((metric) => metric.key),
  );
  const secondaryMetricEntries = getActivityMetricEntries(event).filter(
    (metric) => !primaryMetricKeys.has(metric.key),
  );
  const technicalDetails = {
    id: event.id,
    environment: event.environment,
    source: event.source,
    tenant_refs: {
      platform_tenant_id: event.platform_tenant_id,
      pricing_account_id: event.pricing_account_id,
      pricing_account_key: event.pricing_account_key,
      industrial_tenant_id: event.industrial_tenant_id,
      poker_tenant_id: event.poker_tenant_id,
    },
    frontend_route: event.frontend_route,
    backend_endpoint: event.backend_endpoint,
    http_method: event.http_method,
    http_status_code: event.http_status_code,
    request_id: event.request_id,
    session_id: event.session_id,
    correlation_id: event.correlation_id,
    related_observability_event_id: event.related_observability_event_id,
  };

  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(2,6,23,0.78)',
          backdropFilter: 'blur(6px)',
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: '#0f1b2f',
          backgroundImage: 'none',
          color: '#f8fafc',
          border: `1px solid ${alpha('#38bdf8', 0.22)}`,
          borderRadius: 4,
          boxShadow: '0 28px 80px rgba(0,0,0,0.48)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                {event.message || event.event_type}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.35 }}>
                {event.operation} · {event.event_type}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={event.outcome}
                sx={{
                  fontWeight: 900,
                  ...getStatusChipColor(event.outcome),
                }}
              />
              <Chip
                size="small"
                label={event.severity}
                sx={{
                  bgcolor: '#020617',
                  color: event.severity === 'error' || event.severity === 'critical'
                    ? '#fca5a5'
                    : '#bae6fd',
                  fontWeight: 900,
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            {[
              ['Cuenta / tenant', accountLabel],
              ['Usuario', event.actor_email || event.actor_user_id || '—'],
              ['Fecha', formatDate(event.created_at)],
              ['Módulo', event.app_module],
              ['Área', event.area || '—'],
              ['Motivo', event.reason_code || '—'],
            ].map(([label, value]) => (
              <Paper
                key={label}
                elevation={0}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                  bgcolor: 'rgba(2,6,23,0.28)',
                }}
              >
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.68rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: 0.55,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontSize: '0.84rem',
                    mt: 0.25,
                    wordBreak: 'break-word',
                  }}
                >
                  {value}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
              bgcolor: 'rgba(15,23,42,0.36)',
            }}
          >
            <Stack spacing={1.25}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Impacto operativo
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.25 }}>
                  {formatActivityImpact(event)}
                </Typography>
              </Box>

              {primaryMetricEntries.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 0.85,
                  }}
                >
                  {primaryMetricEntries.map((metric) => (
                    <Box
                      key={metric.key}
                      sx={{
                        borderRadius: 2.25,
                        border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                        bgcolor: 'rgba(2,6,23,0.3)',
                        px: 1,
                        py: 0.85,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.66rem',
                          fontWeight: 900,
                          textTransform: 'uppercase',
                          letterSpacing: 0.45,
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          color: '#f8fafc',
                          fontSize: '1rem',
                          fontWeight: 950,
                          mt: 0.2,
                        }}
                      >
                        {metric.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                  Este evento no incluye métricas operativas estructuradas.
                </Typography>
              )}

              {secondaryMetricEntries.length ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {secondaryMetricEntries.map((metric) => (
                    <Chip
                      key={metric.key}
                      size="small"
                      label={`${metric.label}: ${metric.value}`}
                      sx={{
                        bgcolor: 'rgba(2,6,23,0.42)',
                        color: '#cbd5e1',
                        fontWeight: 800,
                      }}
                    />
                  ))}
                </Stack>
              ) : null}

              <Box
                sx={{
                  borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                  pt: 1.25,
                }}
              >
                <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem', fontWeight: 900, mb: 0.75 }}>
                  Métricas técnicas originales
                </Typography>
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    color: '#64748b',
                    fontSize: '0.72rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(event.metrics ?? {}, null, 2)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
              bgcolor: 'rgba(15,23,42,0.36)',
            }}
          >
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
              Metadata saneada
            </Typography>
            <Typography
              component="pre"
              sx={{
                m: 0,
                color: '#94a3b8',
                fontSize: '0.76rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(event.metadata ?? {}, null, 2)}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
              bgcolor: 'rgba(2,6,23,0.38)',
            }}
          >
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
              Detalle técnico
            </Typography>
            <Typography
              component="pre"
              sx={{
                m: 0,
                color: '#94a3b8',
                fontSize: '0.76rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(technicalDetails, null, 2)}
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#bae6fd',
            borderColor: '#334155',
            textTransform: 'none',
            fontWeight: 900,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActivityTable({
  events,
  tenants,
  onSelectEvent,
}: {
  events: PlatformActivityEventItem[];
  tenants: PlatformTenantListItem[];
  onSelectEvent: (event: PlatformActivityEventItem) => void;
}) {
  const {
    paginatedRows: paginatedEvents,
    pagination,
  } = useAdminTablePagination(events);

  if (!events.length) {
    return (
      <EmptyTableState message="No hay actividad funcional para los filtros actuales." />
    );
  }

  return (
    <>
      <TableContainer sx={adminTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Actividad</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Contexto</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Resultado</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Fecha</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedEvents.map((event) => {
            const accountLabel = getActivityAccountLabel(event, tenants);

            return (
              <TableRow
                key={event.id}
                hover
                onClick={() => onSelectEvent(event)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(56, 189, 248, 0.04)',
                  },
                }}
              >
                <TableCell sx={{ color: '#f8fafc', borderColor: '#334155', maxWidth: 520 }}>
                  <Stack spacing={0.3}>
                    <Typography noWrap sx={{ fontWeight: 900 }}>
                      {event.message || event.event_type}
                    </Typography>
                    <Typography noWrap variant="caption" sx={{ color: '#64748b' }}>
                      {event.operation}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155', maxWidth: 340 }}>
                  <Stack spacing={0.3}>
                    <Typography noWrap variant="body2" sx={{ fontWeight: 800 }}>
                      {event.actor_email || event.actor_user_id || 'Usuario no disponible'}
                    </Typography>
                    <Typography noWrap variant="caption" sx={{ color: '#64748b' }}>
                      {event.app_module} · {accountLabel}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ borderColor: '#334155' }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={event.outcome}
                      sx={{
                        fontWeight: 850,
                        ...getStatusChipColor(event.outcome),
                      }}
                    />
                    {event.severity !== 'info' ? (
                      <Chip
                        size="small"
                        label={event.severity}
                        sx={{
                          bgcolor: '#020617',
                          color: event.severity === 'error' || event.severity === 'critical'
                            ? '#fca5a5'
                            : '#fde68a',
                          fontWeight: 850,
                        }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>

                <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155', whiteSpace: 'nowrap' }}>
                  {formatDate(event.created_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </TableContainer>
      {pagination}
    </>
  );
}

function AuditTable({
  events,
}: {
  events: PlatformAdminAuditEventItem[];
}) {
  const {
    paginatedRows: paginatedEvents,
    pagination,
  } = useAdminTablePagination(events);

  if (!events.length) {
    return <EmptyTableState message="Todavía no hay eventos de auditoría administrativa." />;
  }

  return (
    <>
      <TableContainer sx={adminTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Acción</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Actor</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Estado</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Destino</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Módulo</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 900 }}>Fecha</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedEvents.map((event) => (
            <TableRow key={event.id} hover>
              <TableCell sx={{ color: '#f8fafc', borderColor: '#334155' }}>
                {event.action}
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {event.actor_email || event.actor_user_id || '—'}
              </TableCell>

              <TableCell sx={{ borderColor: '#334155' }}>
                <Chip
                  size="small"
                  label={event.status}
                  sx={{
                    fontWeight: 800,
                    ...getStatusChipColor(event.status),
                  }}
                />
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {event.target_type || event.target_table || '—'}
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {event.module_key || '—'}
              </TableCell>

              <TableCell sx={{ color: '#cbd5e1', borderColor: '#334155' }}>
                {formatDate(event.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
      {pagination}
    </>
  );
}

const PlatformAdminPage: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<PlatformAdminTab>('tenants');
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [activityLens, setActivityLens] = useState<ActivityLens>('attention');
  const [activityOutcome, setActivityOutcome] = useState('');
  const [activitySeverity, setActivitySeverity] = useState('');
  const [activityModule, setActivityModule] = useState('');
  const [activityArea, setActivityArea] = useState('');
  const [activityTenantRef, setActivityTenantRef] = useState('');
  const [activityActorEmail, setActivityActorEmail] = useState('');
  const [activityOperation, setActivityOperation] = useState('');
  const [activityTenantRefInput, setActivityTenantRefInput] = useState('');
  const [activityActorEmailInput, setActivityActorEmailInput] = useState('');
  const [activityOperationInput, setActivityOperationInput] = useState('');
  const [eventStatus, setEventStatus] = useState('');
  const [eventSeverity, setEventSeverity] = useState('');
  const [eventModuleInput, setEventModuleInput] = useState('');
  const [eventModule, setEventModule] = useState('');
  const [selectedTenant, setSelectedTenant] =
    useState<PlatformTenantListItem | null>(null);
  const [selectedActivityEvent, setSelectedActivityEvent] =
    useState<PlatformActivityEventItem | null>(null);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setUserSearch(userSearchInput.trim());
      setActivityTenantRef(activityTenantRefInput.trim());
      setActivityActorEmail(activityActorEmailInput.trim());
      setActivityOperation(activityOperationInput.trim());
      setEventModule(eventModuleInput.trim());
    }, 350);

    return () => window.clearTimeout(debounceTimer);
  }, [
    activityActorEmailInput,
    activityOperationInput,
    activityTenantRefInput,
    eventModuleInput,
    userSearchInput,
  ]);

  const contextQuery = useQuery({
    queryKey: ['platform-admin-context'],
    queryFn: getPlatformAdminContext,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const overviewQuery = useQuery({
    queryKey: ['platform-admin-overview'],
    queryFn: getPlatformAdminOverview,
    retry: false,
    staleTime: 60 * 1000,
  });

  const tenantsQuery = useQuery({
    queryKey: ['platform-admin-tenants'],
    queryFn: getPlatformAdminTenants,
    retry: false,
    staleTime: 60 * 1000,
  });

  const usersQuery = useQuery({
    queryKey: ['platform-admin-users', userSearch],
    queryFn: () =>
      getPlatformAdminUsers({
        limit: 100,
        offset: 0,
        search: userSearch || undefined,
      }),
    placeholderData: (previousData) => previousData,
    retry: false,
    staleTime: 60 * 1000,
  });

  const activityLensParams = getActivityLensParams(activityLens);

  const activityQuery = useQuery({
    queryKey: [
      'platform-admin-activity-events',
      activityLens,
      activityOutcome,
      activitySeverity,
      activityModule,
      activityArea,
      activityTenantRef,
      activityActorEmail,
      activityOperation,
    ],
    queryFn: () =>
      getPlatformAdminActivityEvents({
        limit: 150,
        outcome: activityOutcome || activityLensParams.outcome,
        severity: activitySeverity || activityLensParams.severity,
        app_module: activityModule || activityLensParams.app_module,
        area: activityArea || activityLensParams.area,
        event_type: activityLensParams.event_type,
        operation: activityOperation || undefined,
        actor_email: activityActorEmail || undefined,
        tenant_ref: activityTenantRef || undefined,
      }),
    placeholderData: (previousData) => previousData,
    retry: false,
    staleTime: 30 * 1000,
  });

  const observabilityQuery = useQuery({
    queryKey: [
      'platform-admin-observability-events',
      eventStatus,
      eventSeverity,
      eventModule,
    ],
    queryFn: () =>
      getPlatformAdminObservabilityEvents({
        limit: 100,
        status: eventStatus || undefined,
        severity: eventSeverity || undefined,
        app_module: eventModule || undefined,
      }),
    placeholderData: (previousData) => previousData,
    retry: false,
    staleTime: 30 * 1000,
  });

  const auditQuery = useQuery({
    queryKey: ['platform-admin-audit-events'],
    queryFn: () => getPlatformAdminAuditEvents({ limit: 100 }),
    retry: false,
    staleTime: 30 * 1000,
  });

  const isLoading =
    contextQuery.isLoading ||
    overviewQuery.isLoading ||
    tenantsQuery.isLoading ||
    usersQuery.isLoading;

  const hasError =
    contextQuery.isError ||
    overviewQuery.isError ||
    tenantsQuery.isError ||
    usersQuery.isError;

  const health = overviewQuery.data?.health;
  const tenants = tenantsQuery.data?.tenants ?? [];
  const users = usersQuery.data?.users ?? [];
  const sortedTenants = useMemo(
    () => sortPlatformTenantsForAdmin(tenants),
    [tenants],
  );
  const sortedUsers = useMemo(
    () => sortPlatformUsersForAdmin(users),
    [users],
  );
  const activityEvents = activityQuery.data?.events ?? [];
  const observabilityEvents = observabilityQuery.data?.events ?? [];
  const auditEvents = auditQuery.data?.events ?? [];

  const openObservabilityEvents = useMemo(
    () =>
      observabilityEvents.filter((event) =>
        !event.status || event.status === 'open' || event.status === 'error',
      ).length,
    [observabilityEvents],
  );

  const usersWithErrors = useMemo(
    () =>
      users.filter((user) => user.observability_total_count > 0).length,
    [users],
  );

  const platformHealth = {
    uniqueUsers: health?.unique_users ?? usersQuery.data?.total ?? users.length,
    profiles: health?.profiles ?? 0,

    pricingUsers: health?.pricing_users ?? 0,
    industrialUsers: health?.industrial_users ?? 0,
    pokerUsers: health?.poker_users ?? 0,

    tenantsTotal: health?.tenants_total ?? tenants.length,
    pricingAccounts: health?.pricing_accounts ?? tenants.filter(
      (tenant) => tenant.tenant_type === 'pricing_account',
    ).length,
    industrialTenants: health?.industrial_tenants ?? tenants.filter(
      (tenant) => tenant.tenant_type === 'industrial_costs_tenant',
    ).length,
    platformTenants: health?.platform_tenants ?? tenants.filter(
      (tenant) => tenant.tenant_type === 'customer',
    ).length,

    accessTotal: health?.access_total ?? 0,
    pricingAccess: health?.pricing_access ?? 0,
    industrialAccess: health?.industrial_access ?? 0,
    pokerAccess: health?.poker_access ?? 0,

    openEvents: health?.open_events ?? openObservabilityEvents,
    criticalEvents: health?.critical_events ?? 0,
    affectedUsers: health?.affected_users ?? usersWithErrors,

    subscriptionsTotal: health?.subscriptions_total ?? 0,
    subscriptionsActive: health?.subscriptions_active ?? 0,
    subscriptionsTrial: health?.subscriptions_trial ?? 0,
    subscriptionsPastDue: health?.subscriptions_past_due ?? 0,

    platformAdmins: health?.platform_admins ?? 0,
  };

  const refreshAll = () => {
    void contextQuery.refetch();
    void overviewQuery.refetch();
    void tenantsQuery.refetch();
    void usersQuery.refetch();
    void activityQuery.refetch();
    void observabilityQuery.refetch();
    void auditQuery.refetch();
  };

  const userSearchIsRefreshing =
    usersQuery.isFetching && !usersQuery.isLoading;


  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">
            No se pudo cargar el Administrador General de PROA.
          </Alert>
        </Container>
      </Box>
    );
  }

  const context = contextQuery.data;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#081120',
        color: '#f8fafc',
        px: { xs: 2, md: 3, xl: 4 },
        py: { xs: 2, md: 2.5 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Stack spacing={2.25}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              bgcolor: 'rgba(15, 23, 42, 0.72)',
              px: { xs: 1.5, md: 2 },
              py: 1.25,
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              justifyContent="space-between"
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ minWidth: 0 }}
              >
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/industrial-cost')}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: '#bae6fd',
                    borderColor: 'rgba(56, 189, 248, 0.28)',
                    textTransform: 'none',
                    fontWeight: 850,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#38bdf8',
                      color: '#38bdf8',
                      bgcolor: 'rgba(56, 189, 248, 0.08)',
                    },
                  }}
                >
                  PROA Industrial
                </Button>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    borderColor: 'rgba(148, 163, 184, 0.18)',
                  }}
                />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <AdminPanelSettingsIcon sx={{ color: '#38bdf8', fontSize: 21 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 950,
                        fontSize: { xs: '1rem', md: '1.08rem' },
                        lineHeight: 1.15,
                      }}
                    >
                      PROA Platform Admin
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        color: '#64748b',
                        fontWeight: 750,
                        fontSize: '0.72rem',
                      }}
                    >
                      Gobierno global · usuarios · tenants · errores · auditoría
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label="production"
                  sx={{
                    bgcolor: 'rgba(34, 197, 94, 0.14)',
                    color: '#86efac',
                    fontWeight: 900,
                  }}
                />
                <Chip
                  size="small"
                  label={context?.access?.email}
                  sx={{ bgcolor: '#020617', color: '#f8fafc', fontWeight: 850 }}
                />
                <Chip
                  size="small"
                  label={context?.access?.role}
                  sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 950 }}
                />
                <Tooltip title="Actualizar datos">
                  <IconButton
                    size="small"
                    onClick={refreshAll}
                    sx={{
                      color: '#94a3b8',
                      border: '1px solid rgba(148, 163, 184, 0.18)',
                      '&:hover': {
                        color: '#38bdf8',
                        borderColor: '#38bdf8',
                      },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148, 163, 184, 0.14)',
              bgcolor: 'rgba(15, 23, 42, 0.52)',
              p: 1.25,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              <CompactStatBlock
                label="Usuarios únicos"
                value={platformHealth.uniqueUsers}
                detail={`profiles ${formatMetricValue(platformHealth.profiles)} · pricing ${formatMetricValue(platformHealth.pricingUsers)} · industrial ${formatMetricValue(platformHealth.industrialUsers)} · poker ${formatMetricValue(platformHealth.pokerUsers)}`}
              />
              <CompactStatBlock
                label="Tenants / cuentas"
                value={platformHealth.tenantsTotal}
                detail={`pricing ${formatMetricValue(platformHealth.pricingAccounts)} · industrial ${formatMetricValue(platformHealth.industrialTenants)} · platform ${formatMetricValue(platformHealth.platformTenants)}`}
              />
              <CompactStatBlock
                label="Accesos"
                value={platformHealth.accessTotal}
                detail={`pricing ${formatMetricValue(platformHealth.pricingAccess)} · industrial ${formatMetricValue(platformHealth.industrialAccess)} · poker ${formatMetricValue(platformHealth.pokerAccess)}`}
              />
              <CompactStatBlock
                label="Salud"
                value={platformHealth.openEvents}
                detail={`críticos ${formatMetricValue(platformHealth.criticalEvents)} · afectados ${formatMetricValue(platformHealth.affectedUsers)}`}
                tone={platformHealth.openEvents > 0 ? 'danger' : 'success'}
              />
              <CompactStatBlock
                label="Suscripciones"
                value={platformHealth.subscriptionsTotal}
                detail={`activas ${formatMetricValue(platformHealth.subscriptionsActive)} · trial ${formatMetricValue(platformHealth.subscriptionsTrial)} · vencidas ${formatMetricValue(platformHealth.subscriptionsPastDue)}`}
              />
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...darkPaperSx }}>
            <Tabs
              value={activeTab}
              onChange={(_, nextValue) => setActiveTab(nextValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                borderBottom: '1px solid #334155',
                '& .MuiTab-root': {
                  minHeight: 38,
                  px: 1.6,
                  py: 0.65,
                  color: '#94a3b8',
                  fontWeight: 850,
                  textTransform: 'none',
                },
                '& .Mui-selected': {
                  color: '#38bdf8 !important',
                },
                '& .MuiTabs-indicator': {
                  bgcolor: '#38bdf8',
                },
              }}
            >
              <Tab value="tenants" label="Tenants" />
              <Tab value="users" label="Usuarios globales" />
              <Tab value="overview" label="Resumen" />
              <Tab value="activity" label="Actividad" />
              <Tab value="observability" label="Errores" />
              <Tab value="audit" label="Auditoría" />
            </Tabs>

            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {activeTab === 'overview' ? (
                <Stack spacing={2.25}>
                  <SectionHeader
                    icon={<StorageIcon sx={{ color: '#38bdf8' }} />}
                    title="Resumen operativo"
                    description="Lectura rápida de cuentas, usuarios, errores y actividad administrativa reciente."
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} xl={7}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...darkPaperSx,
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            mb: 1.5,
                          }}
                        >
                          Tenants y cuentas principales
                        </Typography>

                        <TenantTable
                          tenants={sortedTenants.slice(0, 6)}
                          users={sortedUsers}
                          onSelectTenant={setSelectedTenant}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} xl={5}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...darkPaperSx,
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            mb: 1.5,
                          }}
                        >
                          Errores y eventos recientes
                        </Typography>

                        <ObservabilityTable events={observabilityEvents.slice(0, 5)} />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} xl={7}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...darkPaperSx,
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            mb: 1.5,
                          }}
                        >
                          Usuarios visibles
                        </Typography>

                        <UsersTable users={sortedUsers.slice(0, 6)} />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} xl={5}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...darkPaperSx,
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            mb: 1.5,
                          }}
                        >
                          Actividad funcional
                        </Typography>

                        <ActivityTable
                          events={activityEvents.slice(0, 6)}
                          tenants={tenants}
                          onSelectEvent={setSelectedActivityEvent}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              ) : null}

              {activeTab === 'tenants' ? (
                <Stack spacing={3}>
                  <SectionHeader
                    icon={<ApartmentIcon sx={{ color: '#38bdf8' }} />}
                    title="Tenants y usuarios"
                    description="Vista principal de gobierno: abre un tenant para revisar sus módulos, owner y usuarios asociados."
                    right={
                      <Chip
                        label={`${tenants.length} registros`}
                        sx={{ bgcolor: '#0f172a', color: '#f8fafc', fontWeight: 800 }}
                      />
                    }
                  />

                  <TenantTable
                    tenants={sortedTenants}
                    users={sortedUsers}
                    onSelectTenant={setSelectedTenant}
                  />
                </Stack>
              ) : null}

              {activeTab === 'users' ? (
                <Stack spacing={3}>
                  <SectionHeader
                    icon={<PeopleIcon sx={{ color: '#38bdf8' }} />}
                    title="Usuarios globales"
                    description="Búsqueda transversal de usuarios. Para revisar pertenencia y roles por cuenta, usa la vista Tenants."
                    right={
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        {userSearchIsRefreshing ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#7dd3fc',
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Actualizando…
                          </Typography>
                        ) : null}

                        <TextField
                          size="small"
                          placeholder="Buscar usuario, email, empresa..."
                          value={userSearchInput}
                          onChange={(event) => setUserSearchInput(event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: { xs: '100%', sm: 320 },
                            '& .MuiOutlinedInput-root': {
                              color: '#f8fafc',
                              bgcolor: '#0f172a',
                              '& fieldset': { borderColor: '#334155' },
                              '&:hover fieldset': { borderColor: '#38bdf8' },
                            },
                          }}
                        />
                      </Stack>
                    }
                  />

                  <UsersTable users={sortedUsers} />
                </Stack>
              ) : null}

              {activeTab === 'activity' ? (
                <Stack spacing={3}>
                  <SectionHeader
                    icon={<HistoryIcon sx={{ color: '#38bdf8' }} />}
                    title="Actividad funcional"
                    description="Explora acciones relevantes por tenant, usuario, módulo, resultado y operación sin exponer datos sensibles."
                    right={
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {activityQuery.isFetching ? (
                          <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 800 }}>
                            Actualizando…
                          </Typography>
                        ) : null}

                        <Typography sx={{ color: '#94a3b8', fontWeight: 800 }}>
                          {formatMetricValue(activityQuery.data?.total ?? activityEvents.length)} eventos
                        </Typography>
                      </Stack>
                    }
                  />

                  <Paper
                    elevation={0}
                    sx={{
                      ...darkPaperSx,
                      p: 2,
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {ACTIVITY_LENS_OPTIONS.map((option) => {
                          const selected = activityLens === option.value;

                          return (
                            <Button
                              key={option.value}
                              size="small"
                              variant={selected ? 'contained' : 'outlined'}
                              onClick={() => setActivityLens(option.value)}
                              sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 850,
                                bgcolor: selected ? '#38bdf8' : 'transparent',
                                color: selected ? '#020617' : '#bae6fd',
                                borderColor: selected ? '#38bdf8' : '#334155',
                                '&:hover': {
                                  bgcolor: selected ? '#7dd3fc' : 'rgba(56, 189, 248, 0.08)',
                                  borderColor: '#38bdf8',
                                },
                              }}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </Stack>

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Resultado"
                            value={activityOutcome}
                            onChange={(event) => setActivityOutcome(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          >
                            <MenuItem value="">Según vista</MenuItem>
                            <MenuItem value="success">Success</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="partial">Partial</MenuItem>
                            <MenuItem value="blocked">Blocked</MenuItem>
                            <MenuItem value="skipped">Skipped</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Severidad"
                            value={activitySeverity}
                            onChange={(event) => setActivitySeverity(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          >
                            <MenuItem value="">Según vista</MenuItem>
                            <MenuItem value="info">Info</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                            <MenuItem value="error">Error</MenuItem>
                            <MenuItem value="critical">Critical</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Módulo"
                            value={activityModule}
                            onChange={(event) => setActivityModule(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          >
                            <MenuItem value="">Según vista</MenuItem>
                            <MenuItem value="pricing">Pricing</MenuItem>
                            <MenuItem value="industrial_costs">Industrial Costs</MenuItem>
                            <MenuItem value="poker_study">Poker Study</MenuItem>
                            <MenuItem value="platform_admin">Platform Admin</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Área"
                            value={activityArea}
                            onChange={(event) => setActivityArea(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          >
                            <MenuItem value="">Según vista</MenuItem>
                            <MenuItem value="production_reports">Partes de producción</MenuItem>
                            <MenuItem value="production_planning">Planificación</MenuItem>
                            <MenuItem value="plant_users">Usuarios de planta</MenuItem>
                            <MenuItem value="data_upload,import_center,production_reports">
                              Cargas / importaciones
                            </MenuItem>
                            <MenuItem value="access_control">Permisos / accesos</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Tenant / cuenta"
                            placeholder="ID, key o fragmento"
                            value={activityTenantRefInput}
                            onChange={(event) => setActivityTenantRefInput(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Usuario"
                            placeholder="email"
                            value={activityActorEmailInput}
                            onChange={(event) => setActivityActorEmailInput(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Operación"
                            placeholder="upload/import/..."
                            value={activityOperationInput}
                            onChange={(event) => setActivityOperationInput(event.target.value)}
                            sx={{
                              '& .MuiInputLabel-root': { color: '#94a3b8' },
                              '& .MuiOutlinedInput-root': {
                                color: '#f8fafc',
                                bgcolor: '#0f172a',
                                '& fieldset': { borderColor: '#334155' },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                          >
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              La vista rápida limita el ruido. Los filtros manuales permiten investigar por área, cuenta, usuario o módulo.
                            </Typography>

                            <Button
                              size="small"
                              onClick={() => {
                                setActivityOutcome('');
                                setActivitySeverity('');
                                setActivityModule('');
                                setActivityArea('');
                                setActivityTenantRefInput('');
                                setActivityActorEmailInput('');
                                setActivityOperationInput('');
                                setActivityTenantRef('');
                                setActivityActorEmail('');
                                setActivityOperation('');
                              }}
                              sx={{
                                alignSelf: { xs: 'flex-start', sm: 'center' },
                                color: '#bae6fd',
                                textTransform: 'none',
                                fontWeight: 850,
                              }}
                            >
                              Limpiar filtros
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      ...darkPaperSx,
                      p: 0,
                      overflow: 'hidden',
                      minHeight: 260,
                    }}
                  >
                    <Stack spacing={1.25} sx={{ p: 2, pb: 1 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                            Eventos
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                            Lista reducida. Abre una fila para ver impacto, metadata y detalle técnico.
                          </Typography>
                        </Box>

                        {activityQuery.isFetching ? (
                          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: '#7dd3fc' }}>
                            <CircularProgress size={16} />
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 850 }}>
                              Actualizando...
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>

                      {activityQuery.isError ? (
                        <Alert severity="error" variant="outlined">
                          No se pudo cargar la actividad funcional.
                        </Alert>
                      ) : null}
                    </Stack>

                    {!activityQuery.isError ? (
                      <ActivityTable
                        events={activityEvents}
                        tenants={tenants}
                        onSelectEvent={setSelectedActivityEvent}
                      />
                    ) : null}
                  </Paper>
                </Stack>
              ) : null}

              {activeTab === 'observability' ? (
                <Stack spacing={3}>
                  <SectionHeader
                    icon={<ErrorOutlineIcon sx={{ color: '#f87171' }} />}
                    title="Errores y eventos"
                    description="Eventos técnicos, errores y señales operativas capturadas por la plataforma."
                    right={
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          select
                          size="small"
                          label="Estado"
                          value={eventStatus}
                          onChange={(event) => setEventStatus(event.target.value)}
                          sx={{
                            minWidth: 150,
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiOutlinedInput-root': {
                              color: '#f8fafc',
                              bgcolor: '#0f172a',
                              '& fieldset': { borderColor: '#334155' },
                            },
                          }}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="resolved">Resolved</MenuItem>
                          <MenuItem value="error">Error</MenuItem>
                        </TextField>

                        <TextField
                          select
                          size="small"
                          label="Severidad"
                          value={eventSeverity}
                          onChange={(event) => setEventSeverity(event.target.value)}
                          sx={{
                            minWidth: 150,
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiOutlinedInput-root': {
                              color: '#f8fafc',
                              bgcolor: '#0f172a',
                              '& fieldset': { borderColor: '#334155' },
                            },
                          }}
                        >
                          <MenuItem value="">Todas</MenuItem>
                          <MenuItem value="info">Info</MenuItem>
                          <MenuItem value="warning">Warning</MenuItem>
                          <MenuItem value="error">Error</MenuItem>
                          <MenuItem value="critical">Critical</MenuItem>
                        </TextField>

                        <TextField
                          size="small"
                          label="Módulo"
                          value={eventModuleInput}
                          onChange={(event) => setEventModuleInput(event.target.value)}
                          sx={{
                            minWidth: 170,
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiOutlinedInput-root': {
                              color: '#f8fafc',
                              bgcolor: '#0f172a',
                              '& fieldset': { borderColor: '#334155' },
                            },
                          }}
                        />
                      </Stack>
                    }
                  />

                  <ObservabilityTable events={observabilityEvents} />
                </Stack>
              ) : null}

              {activeTab === 'audit' ? (
                <Stack spacing={3}>
                  <SectionHeader
                    icon={<HistoryIcon sx={{ color: '#38bdf8' }} />}
                    title="Auditoría administrativa"
                    description="Registro de cambios realizados desde el administrador general de PROA."
                    right={
                      <Chip
                        label={`${auditEvents.length} eventos`}
                        sx={{ bgcolor: '#0f172a', color: '#f8fafc', fontWeight: 800 }}
                      />
                    }
                  />

                  <AuditTable events={auditEvents} />
                </Stack>
              ) : null}
            </Box>
          </Paper>

          <TenantDetailDialog
            tenant={selectedTenant}
            users={sortedUsers}
            onClose={() => setSelectedTenant(null)}
          />

          <ActivityDetailDialog
            event={selectedActivityEvent}
            tenants={tenants}
            onClose={() => setSelectedActivityEvent(null)}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default PlatformAdminPage;